import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import getRemoteConfigValue from 'utils/firebase/remoteConfig';
// NOTE: Direct import of firebaseService ensures Firebase is fully initialized before we read Remote Config.
// Without this, the bundler may tree-shake the initialization side effects.
import firebaseService from 'utils/firebase/firebase';
import { TutorialConfettiOverlay } from './TutorialConfettiOverlay.web';
import {
    trackWelcomeVideoCompleted,
    trackWelcomeVideoClosed,
    trackTutorialFlowClosed,
    trackTutorialFlowCompleted,
} from 'utils/firebase/analytics';

// Icons - using inline SVGs for simplicity (no additional dependencies)
const XIcon = ({ size = 18, color = '#000' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const PlayIcon = ({ size = 18, color = '#000' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
);

const PauseIcon = ({ size = 18, color = '#000' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
    </svg>
);

const RotateCcwIcon = ({ size = 12, color = '#fff' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    </svg>
);

const SkipBackIcon = ({ size = 12, color = '#000' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="19 20 9 12 19 4 19 20" /><line x1="5" y1="19" x2="5" y2="5" />
    </svg>
);

const SkipForwardIcon = ({ size = 12, color = '#000' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="5 4 15 12 5 20 5 4" /><line x1="19" y1="5" x2="19" y2="19" />
    </svg>
);

const Volume2Icon = ({ size = 15, color = '#fff' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14" /><path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
    </svg>
);

const VolumeXIcon = ({ size = 15, color = '#fff' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" />
    </svg>
);

const Maximize2Icon = ({ size = 13.5, color = '#C5E836' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
    </svg>
);

const Minimize2Icon = ({ size = 13.5, color = '#C5E836' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="4 14 10 14 10 20" /><polyline points="20 10 14 10 14 4" /><line x1="14" y1="10" x2="21" y2="3" /><line x1="3" y1="21" x2="10" y2="14" />
    </svg>
);

// Extract a YouTube video ID from either a raw ID or a full YouTube URL.
function extractYouTubeId(input) {
    if (!input) return '';
    const raw = String(input).trim();
    if (!raw) return '';

    // If it doesn't look like a URL, assume it's already an ID.
    if (!raw.includes('youtube.com') && !raw.includes('youtu.be')) {
        return raw;
    }

    try {
        const url = new URL(raw);
        const host = (url.hostname || '').toLowerCase();
        const parts = (url.pathname || '')
            .split('/')
            .map((part) => part.trim())
            .filter(Boolean);

        // Short youtu.be/<id>
        if (host.includes('youtu.be')) {
            return parts[0] || '';
        }

        if (host.includes('youtube.com') || host.includes('youtube-nocookie.com')) {
            // /shorts/<id>
            const shortsIdx = parts.indexOf('shorts');
            if (shortsIdx !== -1 && parts[shortsIdx + 1]) {
                return parts[shortsIdx + 1];
            }

            // /embed/<id>
            const embedIdx = parts.indexOf('embed');
            if (embedIdx !== -1 && parts[embedIdx + 1]) {
                return parts[embedIdx + 1];
            }

            // /live/<id>
            const liveIdx = parts.indexOf('live');
            if (liveIdx !== -1 && parts[liveIdx + 1]) {
                return parts[liveIdx + 1];
            }

            // watch?v=<id>
            const v = url.searchParams.get('v');
            if (v) return v;
        }
    } catch (e) {
        // Fall through to regex parsing below for malformed URLs.
    }

    try {
        // Fallback for raw strings like ".../shorts/<id>?..."
        const shortsMatch = raw.match(/shorts\/([a-zA-Z0-9_-]+)/i);
        if (shortsMatch?.[1]) return shortsMatch[1];

        const embedMatch = raw.match(/embed\/([a-zA-Z0-9_-]+)/i);
        if (embedMatch?.[1]) return embedMatch[1];

        const vMatch = raw.match(/[?&]v=([a-zA-Z0-9_-]+)/i);
        if (vMatch?.[1]) return vMatch[1];
    } catch (e) {
        // fall through to default
    }

    return raw;
}

// Props:
// isOpen: boolean — whether the modal is visible
// onClose: () => void — callback fired when modal should be closed
// showCloseButton: boolean — whether to show the close button
// isFromOnboarding: boolean — indicates if modal is shown from onboarding flow (tutorial mode)
// videoOverride: optional object for custom single-video mode:
//   { videoInput|video_url|video_id, title?, description? }
export function WelcomeVideoModal({
    isOpen,
    onClose,
    showCloseButton = true,
    isFromOnboarding = false,
    videoOverride = null,
}) {
    const userId = useSelector((state) => state.app?.courseTokenValues?.userId);
    const isRemoteConfigLoaded = useSelector((state) => state.app?.isRemoteConfigLoaded);

    // Responsive sizing
    const [dimensions, setDimensions] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });

    useEffect(() => {
        const handleResize = () => {
            setDimensions({ width: window.innerWidth, height: window.innerHeight });
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const { width, height } = dimensions;
    const isLandscape = width > height;

    // Scaling helper - more generous for web, uses viewport-based scaling
    const rem = useCallback((value) => {
        // For web, use a more reasonable scale that doesn't shrink things too much
        const baseWidth = 1400;
        const scale = Math.max(width, height) / baseWidth;
        return Math.round(value * Math.max(0.8, Math.min(1.5, scale)));
    }, [width, height]);

    // Modal sizing - much larger for web
    const DESIGN_MAX_MODAL_WIDTH = 1400; // Increased from 768
    const horizontalPadding = 40; // Fixed padding for web
    const verticalPadding = 40;
    const availableWidth = Math.max(0, width - horizontalPadding * 2);
    const availableHeight = Math.max(0, height - verticalPadding * 2);
    const headerEstimatedHeight = isLandscape ? 60 : 80;

    // For web: fill up to 95% of available width, capped at max
    let containerWidth = Math.min(availableWidth * 0.95, DESIGN_MAX_MODAL_WIDTH);
    let videoHeight = Math.round((containerWidth * 9) / 16);
    let totalHeight = headerEstimatedHeight + videoHeight;

    // Scale down if video would be too tall
    if (totalHeight > availableHeight * 0.95) {
        const maxVideoHeight = availableHeight * 0.95 - headerEstimatedHeight;
        videoHeight = Math.max(300, maxVideoHeight); // Minimum 300px height
        containerWidth = Math.round((videoHeight * 16) / 9);
    }

    const overrideVideoInput = useMemo(() => {
        const raw = videoOverride?.videoInput
            || videoOverride?.video_url
            || videoOverride?.videoUrl
            || videoOverride?.video_id
            || videoOverride?.videoId
            || '';
        return String(raw || '').trim();
    }, [videoOverride]);

    const isCustomSingleVideoMode = !!overrideVideoInput;
    const isTutorialMode = !!isFromOnboarding;
    const nonTutorialHeaderTitle = String(videoOverride?.title || '').trim() || 'Welcome to Quiklearn!';
    const nonTutorialHeaderSubtitle = String(videoOverride?.description || '').trim() || 'The future of education is here.';
    const backdropColor = isTutorialMode ? 'rgba(3,7,18,0.9)' : '#000000';
    const analyticsSource = isCustomSingleVideoMode ? 'course_entry_video' : 'welcome_modal';

    // Resolve video steps from Remote Config
    const tutorialSteps = useMemo(() => {
        let steps = [];

        // Force Firebase initialization (prevents tree-shaking of the import)
        void firebaseService;

        // Always log BOTH keys so we can compare
        const tutorialRcWeb = getRemoteConfigValue('tutorial_video_ids_web');
        const tutorialRcMobile = getRemoteConfigValue('tutorial_video_ids');
        const welcomeRcWeb = getRemoteConfigValue('welcome_video_id_web');
        const welcomeRcMobile = getRemoteConfigValue('welcome_video_id');

        if (isTutorialMode) {
            const tutorialRc = tutorialRcWeb || tutorialRcMobile;
            const tutorialRaw = tutorialRc?.asString?.() ?? '';

            if (tutorialRaw) {
                try {
                    const parsed = JSON.parse(tutorialRaw);
                    if (Array.isArray(parsed)) {
                        steps = parsed
                            .filter((item) => item && typeof item === 'object')
                            .map((item, index) => {
                                const numericId = typeof item.id === 'number' ? item.id : Number(item.id) || index + 1;
                                const videoId = extractYouTubeId(item.video_id || item.video_url || item.videoId || item.id);
                                const description = String(item.video_description || '').trim();
                                if (!videoId) return null;
                                return { id: numericId, videoId, description };
                            })
                            .filter(Boolean)
                            .sort((a, b) => a.id - b.id);
                    } else if (parsed && typeof parsed === 'object') {
                        steps = Object.entries(parsed)
                            .sort(([aKey], [bKey]) => Number(aKey) - Number(bKey))
                            .map(([key, value], index) => {
                                const videoId = extractYouTubeId(value);
                                if (!videoId) return null;
                                return {
                                    id: Number(key) || index + 1,
                                    videoId,
                                    description: 'Learn the basics of navigation and controls',
                                };
                            })
                            .filter(Boolean);
                    }
                } catch (e) {
                    console.error('❌ Failed to parse tutorial_video_ids_web:', e);
                }
            }

            if (!steps.length) {
                console.log('⚠️ No steps found, falling back to legacy tutorial_video_id');
                const legacyRcValue = getRemoteConfigValue('tutorial_video_id');
                const legacyId = (legacyRcValue?.asString?.() ?? '').trim() || 'FTCzt7K7LGc';
                const videoId = extractYouTubeId(legacyId);
                console.log('  Legacy fallback video ID:', videoId);
                steps = [{ id: 1, videoId, description: 'Learn the basics of navigation and controls' }];
            }
        } else {
            if (isCustomSingleVideoMode) {
                const videoId = extractYouTubeId(overrideVideoInput);
                if (videoId) {
                    steps = [{ id: 1, videoId, description: String(videoOverride?.description || '').trim() }];
                    return steps;
                }
            }

            // Welcome mode: single video
            const welcomeRc = welcomeRcWeb || welcomeRcMobile;
            const welcomeRaw = (welcomeRc?.asString?.() ?? '').trim();
            const videoId = extractYouTubeId(welcomeRaw || 'FTCzt7K7LGc');
            steps = [{ id: 1, videoId, description: '' }];
        }

        return steps;
    }, [isTutorialMode, isRemoteConfigLoaded, isCustomSingleVideoMode, overrideVideoInput, videoOverride?.description]);

    const playerContainerRef = useRef(null);
    const playerRef = useRef(null);
    const playerIntervalRef = useRef(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const totalSteps = tutorialSteps.length;
    const safeIndex = Math.min(Math.max(currentIndex, 0), totalSteps - 1);
    const currentStep = tutorialSteps[safeIndex] || tutorialSteps[0];
    const currentVideoId = currentStep?.videoId || '';
    const currentDescription = currentStep?.description || 'Learn the basics of navigation and controls';

    // Local UI state
    const [isPlaying, setIsPlaying] = useState(false);
    const [hasEnded, setHasEnded] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(100);
    const [isMuted, setIsMuted] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [areControlsVisible, setAreControlsVisible] = useState(true);
    const [showEndTutorialConfirm, setShowEndTutorialConfirm] = useState(false);
    const [showTutorialCompletePopup, setShowTutorialCompletePopup] = useState(false);
    const [completedStepMap, setCompletedStepMap] = useState({});
    const [isPlayerReady, setIsPlayerReady] = useState(false);

    const hideControlsTimeoutRef = useRef(null);
    const progressBarRef = useRef(null);
    const volumeBarRef = useRef(null);

    // Define markCurrentStepCompleted early so it can be used in player init
    const markCurrentStepCompleted = useCallback(() => {
        if (!isTutorialMode) return;
        const id = currentStep?.id;
        if (typeof id === 'undefined' || id === null) return;
        setCompletedStepMap((prev) => {
            const next = prev ? { ...prev } : {};
            if (next[id]) return prev || next;
            next[id] = true;
            return next;
        });
    }, [currentStep?.id, isTutorialMode]);

    // Load YouTube IFrame API
    useEffect(() => {
        if (!isOpen) return;

        // Check if API is already loaded
        if (window.YT && window.YT.Player) {
            return;
        }

        // Load the YouTube IFrame API script
        if (!document.getElementById('youtube-iframe-api')) {
            const tag = document.createElement('script');
            tag.id = 'youtube-iframe-api';
            tag.src = 'https://www.youtube.com/iframe_api';
            const firstScriptTag = document.getElementsByTagName('script')[0];
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        }
    }, [isOpen]);

    // Initialize/update YouTube player when video changes
    useEffect(() => {
        if (!isOpen || !currentVideoId) return;

        const initPlayer = () => {
            // Destroy existing player
            if (playerRef.current) {
                try {
                    playerRef.current.destroy();
                } catch (e) {
                    console.warn('[WelcomeVideoModal] Failed to destroy player', e);
                }
                playerRef.current = null;
            }

            // Clear state for new video
            setCurrentTime(0);
            setDuration(0);
            setHasEnded(false);
            setIsPlayerReady(false);

            // Create container div if needed
            if (!playerContainerRef.current) return;

            // Clear container and add player div
            playerContainerRef.current.innerHTML = '<div id="yt-player-modal"></div>';

            playerRef.current = new window.YT.Player('yt-player-modal', {
                videoId: currentVideoId,
                width: '100%',
                height: '100%',
                playerVars: {
                    autoplay: 1,
                    controls: 0, // Hide YouTube controls, we use custom ones
                    modestbranding: 1,
                    rel: 0,
                    showinfo: 0,
                    fs: 0, // Disable YouTube fullscreen button
                    playsinline: 1,
                },
                events: {
                    onReady: (event) => {
                        console.log('[WelcomeVideoModal] YouTube player ready');
                        setIsPlayerReady(true);
                        const dur = event.target.getDuration();
                        setDuration(dur || 0);
                        setVolume(event.target.getVolume());
                        setIsMuted(event.target.isMuted());
                        event.target.playVideo();
                    },
                    onStateChange: (event) => {
                        const state = event.data;
                        // -1: unstarted, 0: ended, 1: playing, 2: paused, 3: buffering, 5: cued
                        if (state === window.YT.PlayerState.PLAYING) {
                            setIsPlaying(true);
                            setHasEnded(false);
                        } else if (state === window.YT.PlayerState.PAUSED) {
                            setIsPlaying(false);
                        } else if (state === window.YT.PlayerState.ENDED) {
                            console.log('[WelcomeVideoModal] Video ended');
                            setIsPlaying(false);
                            setHasEnded(true);
                            markCurrentStepCompleted();

                            // Auto-advance in tutorial mode
                            if (isTutorialMode && safeIndex < totalSteps - 1) {
                                setCurrentIndex((prev) => Math.min(prev + 1, totalSteps - 1));
                            }
                        }
                    },
                    onError: (event) => {
                        console.error('[WelcomeVideoModal] YouTube player error:', event.data);
                    },
                },
            });
        };

        // Wait for YT API to be ready
        if (window.YT && window.YT.Player) {
            initPlayer();
        } else {
            window.onYouTubeIframeAPIReady = initPlayer;
        }

        return () => {
            if (playerIntervalRef.current) {
                clearInterval(playerIntervalRef.current);
                playerIntervalRef.current = null;
            }
        };
    }, [isOpen, currentVideoId, safeIndex, totalSteps, isTutorialMode, markCurrentStepCompleted]);

    // Poll for current time while playing
    useEffect(() => {
        if (!isPlayerReady || !playerRef.current) return;

        if (playerIntervalRef.current) {
            clearInterval(playerIntervalRef.current);
        }

        playerIntervalRef.current = setInterval(() => {
            if (playerRef.current && typeof playerRef.current.getCurrentTime === 'function') {
                try {
                    const time = playerRef.current.getCurrentTime();
                    const dur = playerRef.current.getDuration();
                    if (typeof time === 'number') setCurrentTime(time);
                    if (typeof dur === 'number' && dur > 0) setDuration(dur);
                } catch (e) {
                    // Player might be destroyed
                }
            }
        }, 250);

        return () => {
            if (playerIntervalRef.current) {
                clearInterval(playerIntervalRef.current);
                playerIntervalRef.current = null;
            }
        };
    }, [isPlayerReady]);

    // Cleanup player when modal closes
    useEffect(() => {
        if (!isOpen) {
            if (playerRef.current) {
                try {
                    playerRef.current.destroy();
                } catch (e) {
                    // Ignore
                }
                playerRef.current = null;
            }
            if (playerIntervalRef.current) {
                clearInterval(playerIntervalRef.current);
                playerIntervalRef.current = null;
            }
            setIsPlayerReady(false);
        }
    }, [isOpen]);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen && isFromOnboarding) {
            setCurrentIndex(0);
            setCurrentTime(0);
            setHasEnded(false);
            setCompletedStepMap({});
            setShowEndTutorialConfirm(false);
            setShowTutorialCompletePopup(false);
        }
    }, [isOpen, isFromOnboarding]);

    // Track tutorial completion
    useEffect(() => {
        if (!isTutorialMode || !showTutorialCompletePopup) return;
        try {
            const completedSteps = Object.keys(completedStepMap || {}).length;
            console.log('[WelcomeVideoModal] Tutorial flow completed (popup visible)', { completedSteps, totalSteps });
            trackTutorialFlowCompleted({
                userId,
                totalSteps,
                completedSteps,
                source: 'onboarding_tutorial',
            });
        } catch (e) {
            console.warn('[Analytics] trackTutorialFlowCompleted failed (effect)', e);
        }
    }, [isTutorialMode, showTutorialCompletePopup, completedStepMap, totalSteps, userId]);

    const isOnLastTutorialStepAndEnded = useMemo(() => {
        if (!isTutorialMode) return false;
        if (!totalSteps || totalSteps <= 0) return false;
        return safeIndex === totalSteps - 1 && !!hasEnded;
    }, [isTutorialMode, totalSteps, safeIndex, hasEnded]);

    const isTutorialFullyCompleted = isOnLastTutorialStepAndEnded;

    const sendToPlayer = useCallback((payload) => {
        try {
            if (!playerRef.current || !isPlayerReady) return;

            switch (payload.type) {
                case 'play':
                    playerRef.current.playVideo();
                    break;
                case 'pause':
                    playerRef.current.pauseVideo();
                    break;
                case 'seekTo':
                    playerRef.current.seekTo(payload.time, true);
                    break;
                case 'setVolume':
                    playerRef.current.setVolume(payload.volume);
                    break;
                case 'mute':
                    playerRef.current.mute();
                    setIsMuted(true);
                    break;
                case 'unmute':
                    playerRef.current.unMute();
                    setIsMuted(false);
                    break;
                default:
                    break;
            }
        } catch (e) {
            console.warn('[WelcomeVideoModal] Failed to control player', e);
        }
    }, [isPlayerReady]);

    const formatTime = useCallback((seconds) => {
        const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
        const mins = Math.floor(safeSeconds / 60);
        const secs = Math.floor(safeSeconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    }, []);

    const resetControlsHideTimer = useCallback(() => {
        if (!isFullscreen) return;
        if (hideControlsTimeoutRef.current) {
            clearTimeout(hideControlsTimeoutRef.current);
        }
        hideControlsTimeoutRef.current = setTimeout(() => {
            setAreControlsVisible(false);
        }, 3000);
    }, [isFullscreen]);

    useEffect(() => {
        if (isFullscreen) {
            setAreControlsVisible(true);
            resetControlsHideTimer();
        } else {
            setAreControlsVisible(true);
            if (hideControlsTimeoutRef.current) {
                clearTimeout(hideControlsTimeoutRef.current);
                hideControlsTimeoutRef.current = null;
            }
        }
        return () => {
            if (hideControlsTimeoutRef.current) {
                clearTimeout(hideControlsTimeoutRef.current);
                hideControlsTimeoutRef.current = null;
            }
        };
    }, [isFullscreen, resetControlsHideTimer]);

    const handlePlayPause = () => {
        if (isFullscreen) {
            setAreControlsVisible(true);
            resetControlsHideTimer();
        }
        setIsPlaying((prev) => {
            const next = !prev;
            sendToPlayer({ type: next ? 'play' : 'pause' });
            return next;
        });
    };

    const handleWatchAgain = () => {
        if (isFullscreen) {
            setAreControlsVisible(true);
            resetControlsHideTimer();
        }
        setCurrentIndex(0);
        setCurrentTime(0);
        setHasEnded(false);
        setIsPlaying(true);
        sendToPlayer({ type: 'seekTo', time: 0 });
        sendToPlayer({ type: 'play' });
    };

    const handlePrev = () => {
        if (!isTutorialMode) return;
        if (safeIndex <= 0) return;
        if (isFullscreen) {
            setAreControlsVisible(true);
            resetControlsHideTimer();
        }
        setCurrentIndex((prev) => Math.max(0, prev - 1));
        setCurrentTime(0);
        setHasEnded(false);
    };

    const handleNext = () => {
        if (safeIndex < totalSteps - 1) {
            setCurrentIndex((prev) => Math.min(prev + 1, totalSteps - 1));
        } else {
            handleRequestClose();
        }
    };


    const canShowWatchAgain = useMemo(() => {
        if (isTutorialMode) return isTutorialFullyCompleted;
        return !!hasEnded;
    }, [isTutorialMode, isTutorialFullyCompleted, hasEnded]);

    const handleRequestClose = useCallback(() => {
        console.log('[WelcomeVideoModal] handleRequestClose', { isTutorialMode, hasEnded, isOnLastTutorialStepAndEnded });

        if (isTutorialMode) {
            if (isOnLastTutorialStepAndEnded) {
                setShowTutorialCompletePopup(true);
            } else {
                setShowEndTutorialConfirm(true);
            }
            return;
        }

        if (!isTutorialMode) {
            if (hasEnded) {
                try {
                    console.log('[WelcomeVideoModal] Closing after video ended – tracking completion');
                    trackWelcomeVideoCompleted({ userId, source: analyticsSource });
                } catch (e) {
                    console.warn('[Analytics] trackWelcomeVideoCompleted failed:', e);
                }
            } else {
                try {
                    console.log('[WelcomeVideoModal] Tracking welcome video closed (not completed)');
                    trackWelcomeVideoClosed({ userId, source: analyticsSource });
                } catch (e) {
                    console.warn('[Analytics] trackWelcomeVideoClosed failed:', e);
                }
            }
        }

        onClose?.();
    }, [isTutorialMode, isOnLastTutorialStepAndEnded, hasEnded, onClose, userId, analyticsSource]);

    // YouTube player events are handled via onStateChange in the player initialization above

    const handleSeekFromPosition = useCallback((clientX, rect) => {
        if (!duration || !rect) return;
        const x = clientX - rect.left;
        const ratio = Math.max(0, Math.min(1, x / rect.width));
        const targetTime = ratio * duration;
        setCurrentTime(targetTime);
        sendToPlayer({ type: 'seekTo', time: targetTime });
        if (isFullscreen) {
            setAreControlsVisible(true);
            resetControlsHideTimer();
        }
    }, [duration, sendToPlayer, isFullscreen, resetControlsHideTimer]);

    const handleVolumeFromPosition = useCallback((clientX, rect) => {
        if (!rect) return;
        const x = clientX - rect.left;
        const ratio = Math.max(0, Math.min(1, x / rect.width));
        const nextVolume = Math.round(ratio * 100);
        setVolume(nextVolume);
        sendToPlayer({ type: 'setVolume', volume: nextVolume });
        if (nextVolume === 0) {
            setIsMuted(true);
        } else if (isMuted) {
            setIsMuted(false);
        }
        if (isFullscreen) {
            setAreControlsVisible(true);
            resetControlsHideTimer();
        }
    }, [sendToPlayer, isMuted, isFullscreen, resetControlsHideTimer]);

    const effectiveVolume = Math.max(0, Math.min(100, isMuted ? 0 : volume));

    const handleToggleFullscreen = useCallback(() => {
        setIsFullscreen((prev) => !prev);
    }, []);

    // For web, use the calculated videoHeight directly (portrait doubling was for mobile)
    const effectiveVideoHeight = videoHeight;

    if (!isOpen) return null;

    const styles = {
        overlay: {
            position: 'fixed',
            inset: 0,
            backgroundColor: backdropColor,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            padding: verticalPadding,
        },
        container: {
            width: isFullscreen ? availableWidth : containerWidth,
            position: 'relative',
        },
        topRightControls: {
            position: 'absolute',
            top: isFullscreen ? rem(6) : -rem(18),
            right: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            zIndex: 10,
        },
        closeButton: {
            width: rem(36),
            height: rem(36),
            borderRadius: rem(12),
            border: '1px solid rgba(190,242,100,0.8)',
            background: 'linear-gradient(135deg, #C5E836, #bef264, #C5E836)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            boxShadow: '0 3px 10px rgba(0,0,0,0.4)',
        },
        fullscreenButton: {
            marginTop: rem(12),
            marginRight: rem(12),
            width: rem(30),
            height: rem(30),
            borderRadius: rem(9),
            border: '1px solid #C5E836',
            backgroundColor: 'rgba(15,23,42,0.9)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
        },
        videoCard: {
            backgroundColor: '#000',
            borderRadius: rem(16),
            overflow: 'hidden',
            border: '1px solid #3f3f46',
        },
        videoWrapper: {
            width: '100%',
            height: isFullscreen ? availableHeight : effectiveVideoHeight,
            backgroundColor: '#000',
            position: 'relative',
        },
        iframe: {
            width: '100%',
            height: '100%',
            border: 'none',
        },
        controlsGradient: {
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.9), rgba(0,0,0,0.5), transparent)',
            padding: rem(18),
            transition: 'opacity 0.2s',
            opacity: (!isFullscreen || areControlsVisible) ? 1 : 0,
            pointerEvents: (!isFullscreen || areControlsVisible) ? 'auto' : 'none',
        },
        progressContainer: {
            marginBottom: rem(12),
        },
        progressTrack: {
            height: rem(6),
            borderRadius: rem(999),
            backgroundColor: 'rgba(255,255,255,0.18)',
            overflow: 'hidden',
            position: 'relative',
            cursor: 'pointer',
        },
        progressFill: {
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%',
            backgroundColor: '#C5E836',
        },
        progressThumb: {
            position: 'absolute',
            width: rem(15),
            height: rem(15),
            borderRadius: '50%',
            border: '2px solid #C5E836',
            backgroundColor: '#fff',
            top: '50%',
            transform: 'translateY(-50%)',
            left: duration > 0 ? `calc(${(currentTime / duration) * 100}% - ${rem(7.5)}px)` : `-${rem(7.5)}px`,
            pointerEvents: 'none',
        },
        timeLabels: {
            marginTop: rem(3),
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: rem(10.5),
            fontFamily: 'Verdana, sans-serif',
            color: '#fff',
        },
        controlsRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        leftControls: {
            display: 'flex',
            alignItems: 'center',
            gap: rem(9),
        },
        rightControls: {
            display: 'flex',
            alignItems: 'center',
            gap: rem(9),
        },
        playButton: {
            width: rem(36),
            height: rem(36),
            borderRadius: '50%',
            backgroundColor: '#C5E836',
            border: '1px solid rgba(190,242,100,0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            boxShadow: '0 3px 10px rgba(0,0,0,0.4)',
        },
        volumeButton: {
            width: rem(30),
            height: rem(30),
            borderRadius: rem(9),
            backgroundColor: 'rgba(15,23,42,0.6)',
            border: '1px solid rgba(148,163,184,0.4)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
        },
        volumeSlider: {
            width: rem(72),
            height: rem(15),
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
        },
        volumeTrack: {
            height: rem(6),
            borderRadius: rem(999),
            backgroundColor: 'rgba(255,255,255,0.18)',
            overflow: 'hidden',
            position: 'relative',
            width: '100%',
        },
        volumeFill: {
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: `${effectiveVolume}%`,
            backgroundColor: '#C5E836',
        },
        navButton: {
            paddingLeft: rem(18),
            paddingRight: rem(18),
            paddingTop: rem(9),
            paddingBottom: rem(9),
            borderRadius: rem(9),
            border: '1px solid #C5E836',
            backgroundColor: '#C5E836',
            display: 'flex',
            alignItems: 'center',
            gap: rem(6),
            cursor: 'pointer',
            fontSize: rem(12),
            fontWeight: 500,
            fontFamily: 'Verdana, sans-serif',
            color: '#000',
        },
        navButtonText: {
            color: '#000',
        },
        navButtonSecondary: {
            paddingLeft: rem(18),
            paddingRight: rem(18),
            paddingTop: rem(9),
            paddingBottom: rem(9),
            borderRadius: rem(9),
            border: '1px solid #C5E836',
            backgroundColor: 'rgba(15,23,42,0.6)',
            display: 'flex',
            alignItems: 'center',
            gap: rem(6),
            cursor: 'pointer',
            fontSize: rem(12),
            fontWeight: 500,
            fontFamily: 'Verdana, sans-serif',
            color: '#fff',
        },
        // Portrait header overlay
        portraitHeader: {
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            backgroundColor: '#000',
            padding: `${rem(24)}px ${rem(18)}px ${rem(20)}px`,
            textAlign: 'center',
        },
        headerTitle: {
            fontSize: rem(18),
            fontWeight: 700,
            fontFamily: 'Verdana, sans-serif',
            color: '#fff',
            marginBottom: rem(8),
        },
        landscapeTitle: {
            fontSize: rem(18),
            fontWeight: 700,
            fontFamily: 'Verdana, sans-serif',
            color: '#fff',
            marginBottom: 0,
            lineHeight: rem(22),
            display: 'inline-flex',
            alignItems: 'center',
            height: rem(22),
        },
        headerSubtitle: {
            fontSize: rem(14),
            fontFamily: 'Verdana, sans-serif',
            color: '#D4D4D8',
            marginBottom: rem(8),
        },
        stepChip: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingLeft: rem(9),
            paddingRight: rem(9),
            paddingTop: rem(3),
            paddingBottom: rem(3),
            borderRadius: rem(999),
            backgroundColor: '#C5E836',
            fontSize: rem(12),
            fontWeight: 500,
            fontFamily: 'Verdana, sans-serif',
            color: '#000',
            lineHeight: rem(16),
        },
        landscapeStepChip: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingLeft: rem(9),
            paddingRight: rem(9),
            paddingTop: 0,
            paddingBottom: 0,
            height: rem(22),
            borderRadius: rem(999),
            backgroundColor: '#C5E836',
            fontSize: rem(12),
            fontWeight: 500,
            fontFamily: 'Verdana, sans-serif',
            color: '#000',
            lineHeight: rem(22),
        },
        // Landscape info below video
        landscapeInfo: {
            marginTop: rem(12),
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        },
        landscapeRow: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: rem(3),
            gap: rem(9),
            alignSelf: 'center',
        },
        // Confirmation modals
        modalBackdrop: {
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.7)',
            backdropFilter: 'blur(3px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10001,
        },
        modalCard: {
            width: rem(280),
            borderRadius: rem(16),
            padding: rem(24),
            backgroundColor: '#111216',
            border: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 12px 24px rgba(0,0,0,0.6)',
        },
        modalTitle: {
            color: '#fff',
            fontWeight: 700,
            textAlign: 'center',
            fontSize: rem(18),
            marginBottom: rem(8),
            fontFamily: 'Verdana, sans-serif',
        },
        modalText: {
            color: 'rgba(255,255,255,0.6)',
            textAlign: 'center',
            fontSize: rem(14),
            marginBottom: rem(20),
            fontFamily: 'Verdana, sans-serif',
        },
        modalButtons: {
            display: 'flex',
            gap: rem(12),
        },
        modalButtonPrimary: {
            flex: 1,
            height: rem(48),
            borderRadius: rem(999),
            backgroundColor: '#C5E836',
            border: '1px solid #C5E836',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: rem(14),
            fontWeight: 700,
            fontFamily: 'Verdana, sans-serif',
            color: '#000',
        },
        modalButtonDanger: {
            flex: 1,
            height: rem(48),
            borderRadius: rem(999),
            backgroundColor: '#b91c1c',
            border: '1px solid rgba(239,68,68,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: rem(14),
            fontWeight: 700,
            fontFamily: 'Verdana, sans-serif',
            color: '#fff',
        },
        modalButtonCentered: {
            height: rem(48),
            paddingLeft: rem(24),
            paddingRight: rem(24),
            borderRadius: rem(999),
            backgroundColor: '#C5E836',
            border: '1px solid #C5E836',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: rem(14),
            fontWeight: 700,
            fontFamily: 'Verdana, sans-serif',
            color: '#000',
            margin: '0 auto',
        },
    };

    return (
        <div style={styles.overlay}>
            {/* Confirm Close Tutorial Modal */}
            {showEndTutorialConfirm && (
                <div style={styles.modalBackdrop} onClick={() => setShowEndTutorialConfirm(false)}>
                    <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalTitle}>Close Tutorial?</div>
                        <div style={styles.modalText}>
                            You can always replay these tutorial videos later by tapping the Help button in the right-side panel.
                        </div>
                        <div style={styles.modalButtons}>
                            <button
                                style={styles.modalButtonPrimary}
                                onClick={() => setShowEndTutorialConfirm(false)}
                            >
                                Resume
                            </button>
                            <button
                                style={styles.modalButtonDanger}
                                onClick={() => {
                                    try {
                                        const completedSteps = Object.keys(completedStepMap || {}).length;
                                        console.log('[WelcomeVideoModal] Tutorial close pressed', {
                                            completedSteps,
                                            totalSteps,
                                            closedAtStepIndex: safeIndex + 1,
                                            closedAtStepId: currentStep?.id,
                                        });
                                        trackTutorialFlowClosed({
                                            userId,
                                            totalSteps,
                                            completedSteps,
                                            closedAtStepIndex: safeIndex + 1,
                                            closedAtStepId: currentStep?.id,
                                            source: 'onboarding_tutorial',
                                        });
                                    } catch (e) {
                                        console.warn('[Analytics] trackTutorialFlowClosed failed (end)', e);
                                    }
                                    setShowEndTutorialConfirm(false);
                                    onClose?.();
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Tutorial Complete Popup */}
            {showTutorialCompletePopup && (
                <div style={styles.modalBackdrop}>
                    <div style={styles.modalCard}>
                        <div style={styles.modalTitle}>Congratulations!</div>
                        <div style={styles.modalText}>
                            Now you know how to use Quiklearn. Enjoy the Adventure.
                        </div>
                        <button
                            style={styles.modalButtonCentered}
                            onClick={() => {
                                setShowTutorialCompletePopup(false);
                                onClose?.();
                            }}
                        >
                            Continue
                        </button>
                    </div>
                    <TutorialConfettiOverlay visible={showTutorialCompletePopup} />
                </div>
            )}

            {/* Main Content */}
            <div style={styles.container}>
                {/* Top-right controls */}
                {showCloseButton && (
                    <div style={styles.topRightControls}>
                        <button style={styles.closeButton} onClick={handleRequestClose}>
                            <XIcon size={rem(18)} color="#000" />
                        </button>
                        <button style={styles.fullscreenButton} onClick={handleToggleFullscreen}>
                            {isFullscreen ? <Minimize2Icon size={rem(13.5)} /> : <Maximize2Icon size={rem(13.5)} />}
                        </button>
                    </div>
                )}

                <div style={styles.videoCard}>
                    <div style={styles.videoWrapper}>
                        {/* YouTube Player Container - API will inject iframe here */}
                        <div
                            ref={playerContainerRef}
                            key={`player-${safeIndex}`}
                            style={styles.iframe}
                        />

                        {/* Portrait overlay header */}
                        {!isFullscreen && !isLandscape && (
                            <div style={styles.portraitHeader}>
                                <div style={styles.headerTitle}>
                                    {isTutorialMode ? (
                                        <>Learn how to<br />Quiklearn with Einstein</>
                                    ) : nonTutorialHeaderTitle}
                                </div>
                                {!isTutorialMode && (
                                    <div style={styles.headerSubtitle}>{nonTutorialHeaderSubtitle}</div>
                                )}
                                {isTutorialMode && (
                                    <>
                                        <div style={styles.headerSubtitle}>{currentDescription}</div>
                                        <div style={styles.stepChip}>{`Step ${safeIndex + 1} of ${totalSteps}`}</div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Tap catcher for fullscreen */}
                        {isFullscreen && !areControlsVisible && (
                            <div
                                style={{ position: 'absolute', inset: 0 }}
                                onClick={() => {
                                    setAreControlsVisible(true);
                                    resetControlsHideTimer();
                                }}
                            />
                        )}

                        {/* Bottom controls */}
                        <div style={styles.controlsGradient}>
                            {/* Progress bar */}
                            <div style={styles.progressContainer}>
                                <div
                                    ref={progressBarRef}
                                    style={styles.progressTrack}
                                    onClick={(e) => handleSeekFromPosition(e.clientX, progressBarRef.current?.getBoundingClientRect())}
                                    onMouseDown={(e) => {
                                        const onMove = (ev) => handleSeekFromPosition(ev.clientX, progressBarRef.current?.getBoundingClientRect());
                                        const onUp = () => {
                                            window.removeEventListener('mousemove', onMove);
                                            window.removeEventListener('mouseup', onUp);
                                        };
                                        window.addEventListener('mousemove', onMove);
                                        window.addEventListener('mouseup', onUp);
                                        handleSeekFromPosition(e.clientX, progressBarRef.current?.getBoundingClientRect());
                                    }}
                                >
                                    <div style={styles.progressFill} />
                                    <div style={styles.progressThumb} />
                                </div>
                                <div style={styles.timeLabels}>
                                    <span>{formatTime(currentTime)}</span>
                                    <span>{formatTime(duration)}</span>
                                </div>
                            </div>

                            {/* Control buttons */}
                            <div style={styles.controlsRow}>
                                <div style={styles.leftControls}>
                                    {!hasEnded && (
                                        <button style={styles.playButton} onClick={handlePlayPause}>
                                            {isPlaying ? <PauseIcon size={rem(18)} /> : <PlayIcon size={rem(18)} />}
                                        </button>
                                    )}

                                    {/* Volume control */}
                                    {!isTutorialFullyCompleted && (
                                        <>
                                            <button
                                                style={styles.volumeButton}
                                                onClick={() => {
                                                    const nextMuted = !isMuted;
                                                    setIsMuted(nextMuted);
                                                    sendToPlayer({ type: nextMuted ? 'mute' : 'unmute' });
                                                }}
                                            >
                                                {isMuted || volume === 0 ? <VolumeXIcon /> : <Volume2Icon />}
                                            </button>
                                            <div
                                                ref={volumeBarRef}
                                                style={styles.volumeSlider}
                                                onClick={(e) => handleVolumeFromPosition(e.clientX, volumeBarRef.current?.getBoundingClientRect())}
                                                onMouseDown={(e) => {
                                                    const onMove = (ev) => handleVolumeFromPosition(ev.clientX, volumeBarRef.current?.getBoundingClientRect());
                                                    const onUp = () => {
                                                        window.removeEventListener('mousemove', onMove);
                                                        window.removeEventListener('mouseup', onUp);
                                                    };
                                                    window.addEventListener('mousemove', onMove);
                                                    window.addEventListener('mouseup', onUp);
                                                    handleVolumeFromPosition(e.clientX, volumeBarRef.current?.getBoundingClientRect());
                                                }}
                                            >
                                                <div style={styles.volumeTrack}>
                                                    <div style={styles.volumeFill} />
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {/* Tutorial: Previous button or Restart Tutorial */}
                                    {isTutorialMode && totalSteps > 1 && (
                                        <>
                                            {!isTutorialFullyCompleted && safeIndex > 0 && (
                                                <button style={styles.navButton} onClick={handlePrev}>
                                                    <SkipBackIcon size={rem(12)} />
                                                    <span style={styles.navButtonText}>Previous</span>
                                                </button>
                                            )}
                                            {isTutorialFullyCompleted && (
                                                <button style={styles.navButtonSecondary} onClick={handleWatchAgain}>
                                                    <RotateCcwIcon size={rem(12)} />
                                                    <span>Restart Tutorial</span>
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>

                                <div style={styles.rightControls}>
                                    {/* Welcome mode: Watch Again */}
                                    {!isTutorialMode && canShowWatchAgain && (
                                        <button style={styles.navButtonSecondary} onClick={handleWatchAgain}>
                                            <RotateCcwIcon size={rem(12)} />
                                            <span>Watch Again</span>
                                        </button>
                                    )}

                                    {/* Tutorial: Next or Start Adventure */}
                                    {isTutorialMode && totalSteps > 1 && !isTutorialFullyCompleted && safeIndex < totalSteps - 1 && (
                                        <button style={styles.navButton} onClick={handleNext}>
                                            <span style={styles.navButtonText}>Next</span>
                                            <SkipForwardIcon size={rem(12)} />
                                        </button>
                                    )}
                                    {isTutorialMode && isTutorialFullyCompleted && (
                                        <button style={styles.navButton} onClick={() => setShowTutorialCompletePopup(true)}>
                                            <span style={styles.navButtonText}>Start Adventure</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Landscape info below video */}
                {!isFullscreen && isTutorialMode && isLandscape && (
                    <div style={styles.landscapeInfo}>
                        <div style={styles.landscapeRow}>
                            <span style={styles.landscapeTitle}>Learn how to Quiklearn with Einstein</span>
                            <span style={styles.landscapeStepChip}>{`Step ${safeIndex + 1} of ${totalSteps}`}</span>
                        </div>
                        <div style={{ ...styles.headerSubtitle, marginBottom: 0 }}>{currentDescription}</div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default WelcomeVideoModal;
